import { type ApolloQueryResult, useMutation, useQuery } from "@apollo/client";
import produce from "immer";
import _ from "lodash";
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import { useTenant } from "@/providers/session";
import {
  type GetInvoiceDetails,
  INVOICE_QUERY,
  type InvoiceDetailsQueryResponse,
  useGetInvoice,
} from "@/queries/invoices/get";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { useCreateLineItem } from "@/queries/line-items/create";
import { useDeleteLineItems } from "@/queries/line-items/delete";
import { useGetAllLineItems } from "@/queries/line-items/fetch";

import {
  ACCOUNTS_QUERY,
  INVOICE_TAGS_MUTATION,
  SUPPLIERS_QUERY,
  TAGS_QUERY,
  TAX_CODES_QUERY,
  UPDATE_MANY_LINE_ITEMS,
  UPDATE_SUPPLIER_MUTATION,
} from "@/apis/gql/apolloClient";
import type {
  AccountDetails,
  Address,
  ExportHistoryType,
  InvoiceData,
  LineItem,
  PaymentDetails,
  Supplier,
  Tags,
  TaxCode,
} from "../../../apis/gql/types";
import { InvoiceValidationProvider } from "./invoice-validation-provider";

export enum UpdateInvoiceFields {
  SupplierChange = "supplierChange",
  SupplierCreate = "supplierCreate",
  SupplierDelete = "supplierDelete",
  Initialize = "initialize",
  AddressId = "addressId",
  AddressLine1 = "addressLine1",
  City = "city",
  State = "state",
  Postcode = "postcode",
  Country = "country",
  PaymentDetailsId = "paymentDetailsId",
  BankName = "bankName",
  IBAN = "IBAN",
  BankCountry = "bankCountry",
  BankAccount = "bankAccount",
  BankCode = "bankCode",
  BIC = "BIC",
  SupplierId = "supplierId",
  SupplierName = "supplierName",
  SupplierStatus = "status",
  Address = "address",
  PaymentDetails = "paymentDetails",
  DatevAccountNumber = "datevAccountNumber",
  VatId = "vatId",
  ExpenseAccount = "accountId",
  LineItemId = "lineItemId",
  AiDescription = "aiDescription",
  Amount = "amount",
  NettoAmount = "nettoAmount",
  Quantity = "quantity",
  Currency = "currency",
  CostCenterId = "costCenterId",
  InvoiceId = "invoiceId",
  ServiceDate = "serviceDate",
  Name = "name",
  VatRate = "vatRate",
  VatRateAndTaxCodeId = "vatRateAndTaxCodeId",
  StartDate = "startDate",
  EndDate = "endDate",
  Transactions = "transactions",
  AccountJustification = "accountJustification",
  SimilarLineItems = "similarLineItems",
  AccountSuggestions = "accountSuggestions",
  Index = "index",
  TaxCodeId = "taxCodeId",
  BuJustification = "buJustification",
  TaxCode = "taxCode",
  BookingPeriod = "bookingPeriod",
  InvoiceText = "invoiceText",
  AiSummary = "aiSummary",
  TotalAmount = "totalAmount",
  DueDate = "dueDate",
  InvoiceDate = "invoiceDate",
  InvoiceNo = "invoiceNo",
  OcrReviewed = "ocrReviewed",
  InvoiceStatus = "invoiceStatus",
  InvoiceSource = "invoiceSource",
  FileBlobName = "fileBlobName",
  LineItems = "lineItems",
  SupplierMatch = "supplierMatch",
  Polygons = "polygons",
  Description = "description",
  DatevBu = "datevBu",
  CreatedAt = "createdAt",
  EditedLineItems = "editedLineItems",
  SimilarInvoices = "similarInvoices",
  Split = "split",
  CreateLineItem = "createLineItem",
  DeleteLineItem = "deleteLineItem",
  CreateDatevPayment = "createDatevPayment",
  Duplicates = "duplicates",
  IsNettoInvoice = "isNettoInvoice",
  ReviewType = "reviewType",
  ParentSupplierId = "parentSupplierId",
  OriginalSupplierName = "originalSupplierName",
}

export enum NewRowIds {
  NewLineItemId = "newLineItemId",
}

interface Action {
  type: UpdateInvoiceFields;
  payload?: any;
  ids?: string[];
}
interface InvoiceReviewState {
  // TODO: Typing here is off, usage of fields added to invoice data should be checked
  invoiceData:
    | (InvoiceData & {
        duplicates?: InvoiceData[];
        invoicePolygon: any;
        InvoicePolygon: any;
        exports: ExportHistoryType[];
        description: string;
        invoice: InvoiceData;
      })
    | undefined;
  suppliers: Supplier[] | undefined;
  accounts: AccountDetails[] | undefined;
  taxCodes: TaxCode[] | undefined;
  boundingBoxes: any[] | undefined;
  editedBoundingBoxIndexes: any[] | undefined;
}
interface InvoiceProviderProps {
  invoiceId: string;
  setCoordinates?: (coordinates?: any) => void;
  children: React.ReactNode; // This type is appropriate for children
}
interface InvoiceContextProps {
  loading: boolean;
  state: InvoiceReviewState;
  dispatch: React.Dispatch<Action>;
  refetch: () => Promise<ApolloQueryResult<any>>;
  refetchLineItems: () => Promise<ApolloQueryResult<any>>;
  updateBox: (polygons?: any, fieldName?: string) => void;
  selectedBoundingBoxIndexes: Set<number>;
  setSelectedBoundingBoxIndexes: React.Dispatch<
    React.SetStateAction<Set<number>>
  >;
  setBoundingBoxes: React.Dispatch<React.SetStateAction<any[]>>;
  setEditedBoundingBoxIndexes: React.Dispatch<React.SetStateAction<any[]>>;
  updateEditedBoundingBoxIndexes: (polygons: any[]) => void;
  handleInvoiceEditSave: (
    fieldName: UpdateInvoiceFields,
    updateValues?: Object,
    ids?: string[],
  ) => Promise<void | string>;
  tags: Tags[];
  tagsLoading: boolean;
  tagsUpdateError: any;
  saveInvoiceTags: (invoiceIds: string[], tagIds: string[]) => Promise<void>;
  updateInvoiceLoading: boolean;
  setLineItems: (lineItems: LineItem[]) => void;
}
interface Polygon {
  coordinates?: number[];
  pageNumber?: string;
}

interface Polygons {
  [fieldName: string]: Polygon;
}

const initialValues = {
  invoiceData: undefined,
  suppliers: undefined,
  accounts: undefined,
  taxCodes: undefined,
  boundingBoxes: undefined,
  editedBoundingBoxIndexes: undefined,
};

const emptySupplier: Supplier = {
  supplierId: undefined,
  supplierName: undefined,
  vatId: undefined,
  status: undefined,
  address: undefined,
  paymentDetails: undefined,
  datevAccountNumber: undefined,
  parentSupplierId: undefined,
  validationErrors: undefined,
  type: undefined,
  customerNumber: undefined,
  promptText: undefined,
};

const emptyAddress: Address = {
  addressId: undefined,
  addressLine1: undefined,
  city: undefined,
  state: undefined,
  postcode: undefined,
  country: undefined,
};

const emptyPaymentDetails: PaymentDetails = {
  paymentDetailsId: undefined,
  bankName: undefined,
  BIC: undefined,
  IBAN: undefined,
  bankAccount: undefined,
  bankCountry: undefined,
  bankCode: undefined,
};

const emptyLineItem = {
  invoiceId: undefined,
  aiDescription: undefined,
  amount: undefined,
  quantity: undefined,
  accountReviewed: undefined,
  accountConfidence: undefined,
  name: undefined,
  vatRate: undefined,
  startDate: undefined,
  endDate: undefined,
  accountId: undefined,
  transactions: undefined,
  accountJustification: undefined,
  similarLineItems: undefined,
  accountSuggestions: undefined,
  index: undefined,
  expenseAccount: undefined,
  invoice: undefined,
  costCenterId: undefined,
  nettoAmount: undefined,
};

// ********* Reducer helper functions ********* //

function setUndefinedFieldsToNull(obj: { [key: string]: any }): void {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key] === undefined) {
        obj[key] = null;
      }
    }
  }
}

const setSupplierField = (
  draft: InvoiceReviewState,
  fieldName: string | number,
  payload: any,
) => {
  draft.invoiceData!.supplier = draft.invoiceData?.supplier || {
    ...emptySupplier,
  };
  draft.invoiceData!.supplier[fieldName] = payload;
};

const setAddressField = (
  draft: InvoiceReviewState,
  fieldName: string | number,
  payload: any,
) => {
  draft.invoiceData!.supplier!.address = draft.invoiceData?.supplier
    ?.address || { ...emptyAddress };
  draft.invoiceData!.supplier!.address[fieldName] = payload;
};

const setPaymentDetailsField = (
  draft: InvoiceReviewState,
  fieldName: string | number,
  payload: any,
) => {
  draft.invoiceData!.supplier!.paymentDetails = draft.invoiceData?.supplier
    ?.paymentDetails || { ...emptyPaymentDetails };
  draft.invoiceData!.supplier!.paymentDetails[fieldName] = payload;
};

const setLineItemField = (
  draft: InvoiceReviewState,
  fieldName: string | number,
  payload: any,
  action: Action,
) => {
  if (
    action.ids?.length === 0 &&
    (draft.invoiceData?.lineItems?.length === 0 ||
      draft.invoiceData?.lineItems === undefined)
  ) {
    draft.invoiceData!.lineItems = [];
    draft.invoiceData!.lineItems.push({
      ...emptyLineItem,
      lineItemId: NewRowIds.NewLineItemId,
      [fieldName]: payload,
    } as LineItem);

    return;
  }

  const lineItems = draft?.invoiceData?.lineItems?.filter(
    (lineItem: any) =>
      action.ids?.includes(lineItem.lineItemId) ||
      lineItem.lineItemId == NewRowIds.NewLineItemId,
  );
  lineItems?.forEach((lineItem: { [x: string]: any }) => {
    lineItem[fieldName] = payload;
  });
};

const reducer = produce((draft: InvoiceReviewState, action: Action) => {
  if (action.type == null) return;
  if (action.payload == undefined) action.payload = null;
  const fieldName = action.type;

  switch (action.type) {
    // initialise state
    case UpdateInvoiceFields.Initialize:
      const newData = action.payload;
      newData.invoiceData = structuredClone(newData.invoiceData);
      Object.assign(draft, newData);
      break;

    // Change duplicate invoices
    case UpdateInvoiceFields.Duplicates:
      draft.invoiceData!.duplicates = action.payload;
      break;

    // change supplier
    case UpdateInvoiceFields.SupplierChange:
    case UpdateInvoiceFields.SupplierCreate:
    case UpdateInvoiceFields.SupplierDelete:
      draft.invoiceData!.supplierId = action.payload?.supplierId;
      draft.invoiceData!.supplier =
        action.type === UpdateInvoiceFields.SupplierDelete
          ? null
          : action.payload;
      break;

    // update supplier fields
    case UpdateInvoiceFields.AddressId:
    case UpdateInvoiceFields.SupplierId:
    case UpdateInvoiceFields.SupplierName:
    case UpdateInvoiceFields.SupplierStatus:
    case UpdateInvoiceFields.ParentSupplierId:
    case UpdateInvoiceFields.OriginalSupplierName:
    case UpdateInvoiceFields.DatevAccountNumber:
    case UpdateInvoiceFields.VatId:
    case UpdateInvoiceFields.Address:
    case UpdateInvoiceFields.PaymentDetails:
      setSupplierField(draft, fieldName, action.payload);
      break;

    case UpdateInvoiceFields.EditedLineItems:
      action.payload.forEach((index: any) => {
        const box = (draft.boundingBoxes ?? []).find(
          (box) => box.index === index,
        );
        if (box) {
          box.processed = true;
        }
      });
      break;

    // update line item fields
    case UpdateInvoiceFields.ExpenseAccount:
      setLineItemField(draft, fieldName, action.payload?.accountId, action);
      setLineItemField(draft, "expenseAccount", action.payload, action);
      break;
    case UpdateInvoiceFields.AiDescription:
    case UpdateInvoiceFields.Amount:
    case UpdateInvoiceFields.NettoAmount:
    case UpdateInvoiceFields.Quantity:
    case UpdateInvoiceFields.Name:
    case UpdateInvoiceFields.VatRate:
    case UpdateInvoiceFields.StartDate:
    case UpdateInvoiceFields.EndDate:
    case UpdateInvoiceFields.Transactions:
    case UpdateInvoiceFields.AccountJustification:
    case UpdateInvoiceFields.SimilarLineItems:
    case UpdateInvoiceFields.AccountSuggestions:
    case UpdateInvoiceFields.Index:
    case UpdateInvoiceFields.TaxCodeId:
    case UpdateInvoiceFields.BuJustification:
      setLineItemField(draft, fieldName, action.payload, action);
      break;

    case UpdateInvoiceFields.TaxCode:
      setLineItemField(draft, "taxCodeId", action.payload?.taxCodeId, action);
      setLineItemField(draft, fieldName, action.payload, action);
      break;

    // update vat details fields

    case UpdateInvoiceFields.CostCenterId:
      setLineItemField(draft, fieldName, action.payload, action);
      break;
    // update core invoice fields
    case UpdateInvoiceFields.LineItems:
      draft.invoiceData!.lineItems = action.payload;
      break;
    default:
      if (fieldName in draft.invoiceData!) {
        (draft.invoiceData![fieldName as keyof InvoiceData] as any) =
          action.payload;
      }
  }
});

const InvoiceContext = createContext<InvoiceContextProps | undefined>(
  undefined,
);

const InvoiceProvider: React.FC<InvoiceProviderProps> = ({
  invoiceId,
  setCoordinates,
  children,
}) => {
  // State Storage
  const [state, dispatch] = useReducer(reducer, initialValues);
  const [refetching, setRefetching] = React.useState(true);
  const [selectedBoundingBoxIndexes, setSelectedBoundingBoxIndexes] =
    React.useState<Set<number>>(new Set<number>());
  const [boundingBoxes, setBoundingBoxes] = React.useState<any[]>([]);

  const [editedBoundingBoxIndexes, setEditedBoundingBoxIndexes] =
    React.useState<any[]>([]);

  const updateEditedBoundingBoxIndexes = (newPolygons: any[]) => {
    //TODO: Want to use this method soon to save editedBoundingBoxIndexes to sessionStorage
    setEditedBoundingBoxIndexes(newPolygons);
  };

  // Mutations
  const [updateInvoice, { loading: updateInvoiceLoading }] = useUpdateInvoice();
  const [updateManyLineItems] = useMutation(UPDATE_MANY_LINE_ITEMS);
  const [createLineItem] = useCreateLineItem();
  const [updateSupplier] = useMutation(UPDATE_SUPPLIER_MUTATION, {
    refetchQueries: [SUPPLIERS_QUERY, INVOICE_QUERY],
  });

  const [updateInvoiceTags, { error: tagsUpdateError }] = useMutation(
    INVOICE_TAGS_MUTATION,
  );

  const { tenantId } = useTenant();

  const deleteLineItems = useDeleteLineItems(tenantId, invoiceId);

  const {
    invoice: invoiceData,
    loading: invoiceQueryLoading,
    error: invoiceError,
    refetch,
  } = useGetInvoice({ invoiceId });

  const { lineItems, refetch: refetchLineItems } =
    useGetAllLineItems(invoiceId);

  const {
    data: suppliersData,
    loading: suppliersLoading,
    error: suppliersError,
    refetch: refetchSuppliers,
  } = useQuery(SUPPLIERS_QUERY, {
    skip: !tenantId || tenantId === "" || !invoiceData, // wait for invoiceData, need the type
    variables: {
      tenantId,
      type:
        invoiceData?.invoiceDetails?.invoice?.type === "receivable"
          ? "debtor"
          : "creditor",
    },
    fetchPolicy: "network-only",
  });
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
  } = useQuery(ACCOUNTS_QUERY, {
    skip: !tenantId || tenantId === "",
    variables: { tenantId },
  });
  const {
    data: taxCodesData,
    loading: taxCodesLoading,
    error: taxCodesError,
  } = useQuery(TAX_CODES_QUERY, {
    skip: !tenantId || tenantId === "",
    variables: { tenantId },
  });

  const {
    data: tagsData,
    loading: tagsLoading,
    error: tagsError,
  } = useQuery(TAGS_QUERY, {
    variables: { tenantId },
  });

  const handleInvoiceEditSave = async (
    fieldName: UpdateInvoiceFields,
    updateValues?: any,
    ids?: string[],
  ): Promise<string | void> => {
    if (fieldName == null) return;
    if (updateValues) setUndefinedFieldsToNull(updateValues);

    switch (fieldName) {
      case UpdateInvoiceFields.CreateLineItem: {
        const createLineItemResponse = await createLineItem({
          invoiceId: state?.invoiceData?.invoiceId,
          lineItem: updateValues,
        });

        return createLineItemResponse?.lineItemId;
      }
      case UpdateInvoiceFields.DeleteLineItem:
        await deleteLineItems(ids ?? [], state.invoiceData?.invoiceId);
        break;
      case UpdateInvoiceFields.SupplierChange:
        await updateInvoice({ invoiceId, ...updateValues });
        await refetch();
        break;

      case UpdateInvoiceFields.SupplierDelete:
        await updateInvoice({ invoiceId, supplierId: null });
        await refetch();
        break;
      case UpdateInvoiceFields.AddressId:
      case UpdateInvoiceFields.SupplierId:
      case UpdateInvoiceFields.SupplierName:
      case UpdateInvoiceFields.SupplierStatus:
      case UpdateInvoiceFields.OriginalSupplierName:
      case UpdateInvoiceFields.ParentSupplierId:
      case UpdateInvoiceFields.DatevAccountNumber:
      case UpdateInvoiceFields.VatId:
      case UpdateInvoiceFields.Address:
      case UpdateInvoiceFields.PaymentDetails:
        await updateSupplier({
          variables: {
            supplierId: state.invoiceData!.supplierId,
            updatedFields: updateValues,
          },
        });
        await refetchSuppliers();
        await refetch();
        break;
      case UpdateInvoiceFields.AddressLine1:
      case UpdateInvoiceFields.City:
      case UpdateInvoiceFields.State:
      case UpdateInvoiceFields.Postcode:
      case UpdateInvoiceFields.Country:
        updateValues = { address: updateValues };
        await updateSupplier({
          variables: {
            supplierId: state.invoiceData!.supplierId,
            updatedFields: updateValues,
          },
        });

        break;
      case UpdateInvoiceFields.PaymentDetailsId:
      case UpdateInvoiceFields.BankName:
      case UpdateInvoiceFields.IBAN:
      case UpdateInvoiceFields.BIC:
      case UpdateInvoiceFields.BankAccount:
      case UpdateInvoiceFields.BankCountry:
      case UpdateInvoiceFields.BankCode:
        updateValues = { paymentDetails: updateValues };
        await updateSupplier({
          variables: {
            supplierId: state.invoiceData!.supplierId,
            updatedFields: updateValues,
          },
        });

        break;
      case UpdateInvoiceFields.ExpenseAccount:
      case UpdateInvoiceFields.AiDescription:
      case UpdateInvoiceFields.Amount:
      case UpdateInvoiceFields.NettoAmount:
      case UpdateInvoiceFields.Quantity:
      case UpdateInvoiceFields.Name:
      case UpdateInvoiceFields.VatRate:
      case UpdateInvoiceFields.StartDate:
      case UpdateInvoiceFields.EndDate:
      case UpdateInvoiceFields.Transactions:
      case UpdateInvoiceFields.AccountJustification:
      case UpdateInvoiceFields.SimilarLineItems:
      case UpdateInvoiceFields.AccountSuggestions:
      case UpdateInvoiceFields.CostCenterId:
      case UpdateInvoiceFields.TaxCodeId:
      case UpdateInvoiceFields.Index: {
        const newLineItem = state.invoiceData?.lineItems.find(
          (linteItem) => linteItem.lineItemId === NewRowIds.NewLineItemId,
        );

        if (
          newLineItem ||
          ids?.[0] === NewRowIds.NewLineItemId ||
          _.isEmpty(ids)
        ) {
          await createLineItem({
            invoiceId: state!.invoiceData!.invoiceId,
            lineItem: {
              ...newLineItem,
              ...updateValues,
              amount: state.invoiceData!.totalAmount ?? undefined,
              name: state.invoiceData!.aiSummary ?? undefined,
            },
          });
        } else {
          await updateManyLineItems({
            variables: { lineItemIds: ids, updatedFields: updateValues },
          });
          await refetchLineItems();
        }
        break;
      }

      case UpdateInvoiceFields.VatRateAndTaxCodeId:
        const { vatRate, taxCodeId } = updateValues as {
          vatRate: number;
          taxCodeId: string;
        };
        const combinedUpdateValues = { vatRate, taxCodeId };

        await updateManyLineItems({
          variables: {
            lineItemIds: ids,
            updatedFields: combinedUpdateValues,
          },
        }).catch(async (e) => {
          await refetch();
        });

        break;
      case UpdateInvoiceFields.InvoiceStatus: {
        const statusRes = await updateInvoice({
          invoiceId: state.invoiceData?.invoiceId,
          ...updateValues,
        });

        dispatch({
          type: UpdateInvoiceFields.Duplicates,
          payload: statusRes?.data?.updateInvoice?.duplicates,
        });
        await refetch(); // FIXME: doing this so as to update the supplier status. Should ideally return the supplier from the mutation itself
        break;
      }
      case UpdateInvoiceFields.ReviewType:
      case UpdateInvoiceFields.BuJustification:
      case UpdateInvoiceFields.TaxCode:
      default:
        const res = await updateInvoice({
          invoiceId: state.invoiceData!.invoiceId,
          ...updateValues,
        });

        dispatch({
          type: UpdateInvoiceFields.Duplicates,
          payload: res?.data?.updateInvoice?.duplicates,
        });
    }
  };

  const handleSetLineItems = (lineItems: LineItem[]) => {
    dispatch({
      type: UpdateInvoiceFields.LineItems,
      payload: lineItems,
    });
  };

  useEffect(() => {
    if (invoiceId) {
      refetch()
        .then(() => {
          setRefetching(false);
        })
        .catch((e) => {
          console.log("error fetching invoice", e);
        });
    }
  }, [invoiceId]);

  // Initialize State
  useEffect(() => {
    if (
      invoiceData &&
      suppliersData &&
      accountsData &&
      taxCodesData &&
      boundingBoxes &&
      editedBoundingBoxIndexes &&
      invoiceData.invoiceDetails &&
      lineItems
    ) {
      const _invoiceData = structuredClone(
        invoiceData,
      ) as InvoiceDetailsQueryResponse & {
        invoiceDetails: GetInvoiceDetails & {
          lineItemDetails: { lineItem: LineItem }[];
        };
      };
      const matchingSupplier = suppliersData.suppliers.find(
        (supplier: Supplier) =>
          supplier.supplierId ===
          _invoiceData.invoiceDetails.invoice.supplierId,
      );

      _invoiceData.invoiceDetails.lineItemDetails = lineItems;

      const payload = {
        invoiceData: {
          ...state.invoiceData,
          ..._invoiceData.invoiceDetails,
          ..._invoiceData.invoiceDetails.invoice,
          lineItems: _invoiceData?.invoiceDetails?.lineItemDetails?.map(
            (lineItemDetail: any) => {
              return {
                ...lineItemDetail,
                ...lineItemDetail?.lineItem,
              };
            },
          ),
        },
        suppliers: suppliersData.suppliers,
        accounts: accountsData.accounts,
        taxCodes: taxCodesData.taxCodes,
        boundingBoxes: boundingBoxes,
        editedBoundingBoxIndexes: editedBoundingBoxIndexes,
      };

      payload.invoiceData.supplier = matchingSupplier ?? null;
      dispatch({ type: UpdateInvoiceFields.Initialize, payload });
    }
  }, [
    invoiceData,
    suppliersData,
    accountsData,
    taxCodesData,
    boundingBoxes,
    editedBoundingBoxIndexes,
    lineItems,
  ]);

  const handleInvoiceTagsSave = async (
    invoiceIds: InvoiceData["invoiceId"][],
    tagIds: string[],
  ) => {
    await updateInvoiceTags({
      variables: { invoiceIds, tagIds },
    });
  };

  const values = useMemo(() => {
    if (!state.invoiceData) return undefined;

    return {
      invoiceId: state.invoiceData.invoiceId,
      invoiceNo: state.invoiceData.invoiceNo,
      totalAmount: state.invoiceData.totalAmount,
      currency: state.invoiceData.currency,
      split: state.invoiceData.split,
      isNettoInvoice: state.invoiceData.isNettoInvoice,
      supplierId: state.invoiceData.supplierId,
      invoiceDate: state.invoiceData.invoiceDate
        ? new Date(state.invoiceData.invoiceDate)
        : null,
      serviceDate: state.invoiceData.serviceDate
        ? new Date(state.invoiceData.serviceDate)
        : null,
    };
  }, [state.invoiceData]);

  function updateBox(polygons?: Polygons, fieldName?: string) {
    if (!setCoordinates) {
      return;
    }

    if (!fieldName || !polygons) {
      setCoordinates(undefined);
      return;
    }

    const polygon = _.get(polygons, fieldName);

    if (!polygon?.coordinates) {
      setCoordinates(undefined);
      return;
    }

    const left = polygon?.coordinates?.[0];
    const top = polygon?.coordinates?.[1];
    const right = polygon?.coordinates?.[2];
    const bottom = polygon?.coordinates?.[5];
    const pageNumber = Number.parseInt(polygon?.pageNumber ?? "");

    // Calculate width and height
    const width = right * 72 - left * 72;
    const height = bottom * 72 - top * 72;

    setCoordinates({
      left: left * 72,
      top: top * 72,
      width,
      height,
      pageNumber,
    });
  }

  return (
    <InvoiceContext.Provider
      value={{
        loading: invoiceQueryLoading || refetching,
        state,
        dispatch,
        refetch,
        handleInvoiceEditSave,
        updateBox,
        selectedBoundingBoxIndexes,
        setSelectedBoundingBoxIndexes,
        setBoundingBoxes: setBoundingBoxes,
        setEditedBoundingBoxIndexes: setEditedBoundingBoxIndexes,
        updateEditedBoundingBoxIndexes: updateEditedBoundingBoxIndexes,
        tags: tagsData?.tags ?? [],
        tagsLoading,
        tagsUpdateError,
        saveInvoiceTags: handleInvoiceTagsSave,
        updateInvoiceLoading,
        refetchLineItems,
        setLineItems: handleSetLineItems,
      }}
    >
      <InvoiceValidationProvider invoice={values}>
        {children}
      </InvoiceValidationProvider>
    </InvoiceContext.Provider>
  );
};

export const useInvoiceReviewContext = () => {
  const context = useContext(InvoiceContext);
  if (!context)
    throw new Error("useStateContext must be used within a StateProvider");
  return {
    invoiceReviewState: context.state,
    refetch: context.refetch,
    updateBox: context.updateBox,
    selectedBoundingBoxIndexes: context.selectedBoundingBoxIndexes,
    setSelectedBoundingBoxIndexes: context.setSelectedBoundingBoxIndexes,
    setBoundingBoxes: context.setBoundingBoxes,
    setEditedBoundingBoxIndexes: context.setEditedBoundingBoxIndexes,
    updateEditedBoundingBoxIndexes: context.updateEditedBoundingBoxIndexes,
    loading: context.loading,
    tags: context.tags,
    tagsLoading: context.tagsLoading,
    tagsUpdateError: context.tagsUpdateError,
    saveInvoiceTags: context.saveInvoiceTags,
    refetchLineItems: context.refetchLineItems,
    updateInvoiceLoading: context.updateInvoiceLoading,
    setLineItems: context.setLineItems,
  };
};

export const useInvoiceReviewStateDispatch = () => {
  const context = useContext(InvoiceContext);
  if (!context)
    throw new Error("useStateDispatch must be used within a StateProvider");
  return context.dispatch;
};

export const useSaveInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context)
    throw new Error("useStateDispatch must be used within a StateProvider");
  return { saveInvoice: context.handleInvoiceEditSave };
};

export default memo(InvoiceProvider);
